import React from 'react';
import {
  DEFAULT_PAGE_SIZE,
  getPaginationMeta,
  getVisiblePageNumbers,
} from '../../utils/pagination';

function ChevronIcon({ direction = 'left' }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={direction === 'right' ? 'rotate-180' : ''}
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function TablePagination({
  totalCount,
  currentPage,
  onPageChange,
  pageSize = DEFAULT_PAGE_SIZE,
}) {
  const { totalPages } = getPaginationMeta(totalCount, currentPage, pageSize);

  if (totalCount <= pageSize) return null;

  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const visiblePages = getVisiblePageNumbers(safePage, totalPages);

  const btn =
    'inline-flex h-8 min-w-[32px] items-center justify-center rounded-md px-2 text-sm transition disabled:pointer-events-none disabled:opacity-40';

  return (
    <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
      <nav
        className="inline-flex flex-wrap items-center justify-center gap-1"
        aria-label="Table pagination"
      >
        <button
          type="button"
          disabled={safePage === 1}
          onClick={() => onPageChange(safePage - 1)}
          className={`${btn} gap-1 border border-gray-200 bg-white px-2.5 text-gray-600 hover:border-brand/30 hover:text-brand`}
          aria-label="Previous page"
        >
          <ChevronIcon direction="left" />
          <span className="text-xs font-medium">Prev</span>
        </button>

        {visiblePages.map((p, i, arr) => {
          const prev = arr[i - 1];
          const showEllipsis = prev != null && p - prev > 1;
          const isActive = safePage === p;

          return (
            <React.Fragment key={p}>
              {showEllipsis ? (
                <span className="px-0.5 text-xs text-gray-400">…</span>
              ) : null}
              <button
                type="button"
                onClick={() => onPageChange(p)}
                aria-label={`Page ${p}`}
                aria-current={isActive ? 'page' : undefined}
                className={`${btn} font-medium ${
                  isActive
                    ? 'bg-brand text-white'
                    : 'border border-gray-200 bg-white text-gray-700 hover:border-brand/30 hover:text-brand'
                }`}
              >
                {p}
              </button>
            </React.Fragment>
          );
        })}

        <button
          type="button"
          disabled={safePage === totalPages}
          onClick={() => onPageChange(safePage + 1)}
          className={`${btn} gap-1 border border-gray-200 bg-white px-2.5 text-gray-600 hover:border-brand/30 hover:text-brand`}
          aria-label="Next page"
        >
          <span className="text-xs font-medium">Next</span>
          <ChevronIcon direction="right" />
        </button>
      </nav>

      <span className="text-xs tabular-nums text-gray-400">
        {safePage}/{totalPages}
      </span>
    </div>
  );
}

export default TablePagination;
